import { AiAskResponse } from "@/provider/ai/type";
import { Rating } from "./rating";
import { PplxResponse } from "@/provider/perplexity/type";
import React from "react";
import { Button } from "@/components/ui/button";
import Markdown from "react-markdown";
import { MdOutlinePublic, MdOutlinePublicOff } from "react-icons/md";
import { cn } from "@/lib/utils";

// Custom link component that opens in a new tab
const CustomLink = (props: any) => (
  <a {...props} target="_blank" rel="noopener noreferrer" />
);

const ThreadAnswer = ({
  threadLoading,
  result,
  setResult,
  keywords,
  ticker,
  initialQuestion,
  extResult,
  setExtResult,
  setExtResultLoading,
}: {
  drawerOpen: boolean;
  setDrawerOpen: (value: boolean) => void;
  threadLoading: boolean;
  setThreadLoading: (value: boolean) => void;
  result?: AiAskResponse;
  setResult: (value: AiAskResponse) => void;
  question: string;
  keywords?: string;
  ticker?: string;
  initialQuestion: string;
  extResult?: undefined | null | PplxResponse;
  setExtResult?: (value: PplxResponse) => void;
  setExtResultLoading: (value: boolean) => void;
}) => {
  return (
    <>
      <div className="md:px:3 flex h-96 w-full flex-col items-center justify-center px-1.5">
        <div className="h-full w-full">
          <div className="flex h-full w-full items-center justify-center px-0">
            <div className="relative h-full w-full rounded-lg">
              <Answer
                threadLoading={threadLoading}
                result={result}
                setResult={setResult}
                question={initialQuestion}
                keywords={keywords}
                ticker={ticker}
                extResult={extResult}
                setExtResult={setExtResult}
                setExtResultLoading={setExtResultLoading}
              ></Answer>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

function Answer({
  threadLoading,
  result,
  setResult,
  question,
  keywords,
  ticker,
  extResult,
}: {
  threadLoading: boolean;
  result?: AiAskResponse;
  setResult: (value: AiAskResponse) => void;
  question: string;
  keywords?: string;
  ticker?: string;
  extResult?: undefined | null | PplxResponse;
  setExtResult?: undefined | ((value: PplxResponse) => void);
  setExtResultLoading?: (value: boolean) => void;
}) {
  const [showPublic, setShowPublic] = React.useState(false);
  return (
    <div>
      {threadLoading ? (
        <div className="flex h-96 items-center justify-center space-x-2 dark:invert">
          <span className="sr-only">Loading...</span>
          <div className="h-2 w-2 animate-bounce rounded-full bg-black"></div>
          <div className="h-2 w-2 animate-bounce rounded-full bg-black [animation-delay:-0.3s]"></div>
          <div className="h-2 w-2 animate-bounce rounded-full bg-black [animation-delay:-0.15s]"></div>
        </div>
      ) : (
        <div className={"flex w-full"}>
          <div className={"flex w-full flex-col"}>
            <div className={"mb-1 flex flex-row items-center justify-between"}>
              <p className="text-wrap text-lg font-bold capitalize">
                {question}
                {question.endsWith("?") || !question ? "" : "?"}
              </p>
              <div className="flex flex-row items-center space-x-2">
                <Button
                  onClick={() => setShowPublic(!showPublic)}
                  className="focus:ring-3 bg-blue-300 text-sm text-blue-950"
                  variant={"outline"}
                  title={
                    showPublic
                      ? "Hide public information"
                      : "Show public information"
                  }
                >
                  {showPublic ? <MdOutlinePublic /> : <MdOutlinePublicOff />}
                </Button>
              </div>
            </div>
            {!showPublic && (
              <div
                className={
                  "h-[calc(100vh-20rem)] w-full flex-col overflow-y-scroll rounded-md bg-white p-2 ring-1"
                }
              >
                <div>
                  <Markdown
                    className="h-full select-text text-wrap pl-5 text-sm font-normal leading-7"
                    components={{ a: CustomLink }}
                  >
                    {result?.generated_summary}
                  </Markdown>
                </div>
                <p
                  className={cn(
                    result?.generated_summary &&
                      result?.generated_summary !== ""
                      ? "mt-4 text-sm italic leading-5"
                      : "text-sm italic leading-5",
                  )}
                >
                  {result?.generated_summary &&
                  result?.generated_summary !== "" &&
                  showPublic
                    ? `[Answers are based on public information]`
                    : `[Answers will be based on proprietary information]`}
                </p>
                <div className="mt-4 flex flex-col justify-end">
                  <span className="flex flex-row">
                    {keywords && result?.id && (
                      <p className="text-xsm italic text-slate-400">{`\`${keywords}\` used as keywords to narrow down the number of articles. Ordered by relevance/hits.`}</p>
                    )}
                    {ticker && result?.id && (
                      <p className="mb-2 text-xsm italic text-slate-400">{`\`${ticker}\` used as the company ticker for finding relevant results.`}</p>
                    )}
                  </span>
                  {result?.hint && (
                    <p className="text-xsm text-slate-400">{result?.hint}</p>
                  )}
                  {result?.model && (
                    <p className="text-xsm text-slate-400">
                      {result?.model} was used.
                    </p>
                  )}
                  <div className="">
                    {result?.id && (
                      <Rating
                        rating={0}
                        feedbackId={result.id!}
                        setFeedbackId={(id) => {
                          setResult({ ...result, id: id });
                        }}
                        model={result.model}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
            {showPublic && (
              <div
                className={
                  "h-[calc(100vh-20rem)] w-full flex-col overflow-y-scroll rounded-md bg-white p-2"
                }
              >
                {extResult &&
                  extResult.choices?.map((c) => {
                    return (
                      <div className={"relative w-full"} key={c.index}>
                        <p className={"mb-2 text-blue-500 underline"}>
                          Public information
                        </p>
                        <Markdown
                          className="h-64 list-inside text-wrap px-1 text-sm md:px-6"
                          components={{ a: CustomLink }}
                        >
                          {c.message.content}
                        </Markdown>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ThreadAnswer;
